import {
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  EnumUserStatus,
  UserAccessOperators,
  UserWithPictureTc,
} from "gql/generated";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const ROLE_TEXT_COLOR_MAP: Map<EnumUserAccessOperatorsRole, string> = new Map([
  [EnumUserAccessOperatorsRole.Owner, "text.accent.blue.mid"],
  [EnumUserAccessOperatorsRole.Editor, "text.accent.magenta.mid"],
  [EnumUserAccessOperatorsRole.ViewOnly, "text.success.mid"],
  [EnumUserAccessOperatorsRole.NoAccess, "text.light"],
  [EnumUserAccessOperatorsRole.Developer, "text.accent.purple.mid"],
]);

const ROLE_TAG_COLOR_MAP: Map<EnumUserAccessOperatorsRole, string> = new Map([
  [EnumUserAccessOperatorsRole.Owner, "accent.blue"],
  [EnumUserAccessOperatorsRole.Editor, "accent.magenta"],
  [EnumUserAccessOperatorsRole.ViewOnly, "success"],
  [EnumUserAccessOperatorsRole.NoAccess, "primary"],
  [EnumUserAccessOperatorsRole.Developer, "accent.purple"],
]);

type UseStaffRoleAndStatusProps = {
  user?: Partial<UserWithPictureTc>;
  entityId: string;
};

export const useStaffRoleAndStatus = ({
  user,
  entityId,
}: UseStaffRoleAndStatusProps) => {
  const { t } = useTranslation("core", {
    keyPrefix: "STAFF.ADD_EDIT_PERSON.LABELS",
  });

  // Get the access document for the matching entity
  const entityAccess: UserAccessOperators | null | undefined =
    user?.access?.operators?.find(item => item?.entityId === entityId) ??
    user?.access?.organizations?.find(item => item?.entityId === entityId) ??
    undefined;

  const enrollmentStatus = user?.status as EnumUserStatus | undefined;

  const {
    role,
    roleDisplay,
    roleTextColor,
    roleTagColor,
    status,
    isActive,
    statusDisplay,
    statusColor,
  } = useMemo(() => {
    const role =
      entityAccess?.role === EnumUserAccessOperatorsRole.Staff
        ? EnumUserAccessOperatorsRole.Editor
        : entityAccess?.role;
    const roleDisplay = role ? t(`ROLE_DISPLAY_${role}`) : "";
    const roleTextColor = role ? ROLE_TEXT_COLOR_MAP.get(role) : "text.light";
    const roleTagColor = role ? ROLE_TAG_COLOR_MAP.get(role) : "primary";

    const status = entityAccess?.status;
    const isActive =
      status === EnumUserAccessOperatorsStatus.Accepted &&
      enrollmentStatus === EnumUserStatus.Enrolled;

    let statusDisplay: string;
    let statusColor: string;

    if (isActive) {
      statusDisplay = t("STATUS_ACTIVE");
      statusColor = "success";
    } else if (
      status === EnumUserAccessOperatorsStatus.Accepted &&
      enrollmentStatus === EnumUserStatus.Provisioned
    ) {
      statusDisplay = t("STATUS_INVITED");
      statusColor = "accent.magenta";
    } else if (
      status === EnumUserAccessOperatorsStatus.Accepted &&
      role === EnumUserAccessOperatorsRole.NoAccess
    ) {
      statusDisplay = t("STATUS_MEMBER");
      statusColor = "primary";
    } else if (status === EnumUserAccessOperatorsStatus.Accepted) {
      statusDisplay = t("STATUS_NOT_INVITED");
      statusColor = "primary";
    } else if (status === EnumUserAccessOperatorsStatus.Invited) {
      statusDisplay = t("STATUS_INVITED");
      statusColor = "accent.magenta";
    } else if (status === EnumUserAccessOperatorsStatus.Requested) {
      statusDisplay = t("STATUS_REQUESTED");
      statusColor = "accent.purple";
    } else if (
      status === EnumUserAccessOperatorsStatus.NotInvited &&
      role === EnumUserAccessOperatorsRole.NoAccess
    ) {
      statusDisplay = t("STATUS_MEMBER");
      statusColor = "primary";
    } else if (status === EnumUserAccessOperatorsStatus.NotInvited) {
      statusDisplay = t("STATUS_NOT_INVITED");
      statusColor = "primary";
    } else {
      statusDisplay = t("STATUS_UNKNOWN");
      statusColor = "primary";
    }

    return {
      role,
      roleDisplay,
      roleTextColor,
      roleTagColor,
      status,
      isActive,
      statusDisplay,
      statusColor,
    };
  }, [entityAccess, enrollmentStatus, t]);

  return {
    role,
    roleDisplay,
    roleTextColor,
    roleTagColor,
    status,
    isActive,
    statusDisplay,
    statusColor,
  };
};
